/**
 * The page's modes and the one mechanism that paints them (`foundation/theme/README.md`;
 * docs/decisions/2026-11-03-panel-day-and-night-everywhere.md).
 *
 * The Panel has one palette (owner, 4 November 2026): one day palette and one night palette of the same names. Each screen
 * declares only its mode policy in `screens/<screen>/page-theme.ts`: `both`, `day-only` or `night-only`. Its binding
 * applies it before the screen's first paint (`app/screen-bindings/binding.tsx`): `<html data-mode="day|night">`. Every
 * colour is a custom property under that attribute (`_base.scss`), so the screen, its portals and the shell follow with
 * no attribute of their own: no page has a scope, a palette or a colour of its own. The device's choice (`localStorage["exylon.panel.tono"]` = `dia` | `noche`,
 * default day, never sent to the server) applies only where the page has both modes; a fixed page ignores it and leaves
 * it stored. The shell draws the switch only where the page has both (`pageAllowsSwitch`).
 *
 * This file is in the Panel's entry (the shell and the doors read it); the switch's setter and hooks live in `mode.ts`.
 */

import { useLayoutEffect, useSyncExternalStore } from 'react';

import { readDeviceChoice, writeDeviceChoice } from '../../runtime/durable-work/device-choice';

/** A page's mode policy: it follows the switch, or it is fixed to the day or the night of the one palette. */
type PageModes = 'both' | 'day-only' | 'night-only';
export type ThemeMode = 'day' | 'night';

export interface PageTheme {
  readonly modes: PageModes;
}

/**
 * The doors drawn without a screen (sign-in, session expired, cannot open: `AccesoStage`) follow the device's choice
 * (`both`); their night values load with the lazy night sheet (`night-lazy.scss`, imported by `acceso-door.tsx`).
 */
export const DOOR_THEME: PageTheme = { modes: 'both' };

/** The device's memory of the choice: `"dia"` | `"noche"` (the key Analítica's switch already used). */
const MODE_STORAGE_KEY = 'exylon.panel.tono';
/** What the boot script (`public/mode-boot.js`) reads to paint the right mode before the app starts: per path, the page's
    policy (`{ "<path>": { "m": "both" }, "*": { "m": "night" } }`; `*` is the last mode painted). */
const PAINT_CACHE_KEY = 'exylon.panel.tono.paginas';

let page: PageTheme | null = null;
let chosen: ThemeMode | null = null;
const listeners = new Set<() => void>();

/** The device's choice: day when nothing is stored, the store is unreadable or the value is unknown. */
export function chosenMode(): ThemeMode {
  chosen ??= readDeviceChoice(MODE_STORAGE_KEY) === 'noche' ? 'night' : 'day';
  return chosen;
}

/** The mode a page paints: its own when it has one, the device's choice when it has both. Day before any page. */
function effectiveMode(theme: PageTheme | null = page): ThemeMode {
  if (theme === null) return 'day';
  if (theme.modes === 'both') return chosenMode();
  return theme.modes === 'night-only' ? 'night' : 'day';
}

/** Whether the switch may be drawn: the page has both modes. A switch that does nothing is never drawn. */
export const pageAllowsSwitch = (): boolean => page?.modes === 'both';

export function subscribePageTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

const notify = () => { for (const listener of listeners) listener(); };

/** A path as the paint cache keys it: an id segment (a UUID) is written `:id`. */
const paintPath = (path: string): string => path.replace(/[0-9a-f]{8}-[0-9a-f-]{27}/gi, ':id');

function remember(theme: PageTheme, mode: ThemeMode): void {
  let cache: Record<string, { m: string }> = {};
  try { cache = JSON.parse(readDeviceChoice(PAINT_CACHE_KEY) ?? '{}') as typeof cache; } catch { cache = {}; }
  cache[paintPath(location.pathname)] = { m: theme.modes };
  cache['*'] = { m: mode };
  writeDeviceChoice(PAINT_CACHE_KEY, JSON.stringify(cache));
}

let painted = false;

/** Paints the current page: the mode, the browser's theme colour, the paint cache. */
function paint(): void {
  if (typeof document === 'undefined' || page === null) return;
  const root = document.documentElement;
  const mode = effectiveMode();
  root.setAttribute('data-mode', mode);
  // A boot script cached before the scopes were deleted may have set the retired `data-scope`: removed once, on the
  // first paint (no stylesheet reads it; `scripts/theme-check.mjs` forbids it).
  if (!painted) root.removeAttribute('data-scope');
  painted = true;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta !== null) {
    const ground = getComputedStyle(root).getPropertyValue('--color-bg').trim();
    if (ground !== '') meta.setAttribute('content', ground);
  }
  remember(page, mode);
}

/** @internal Applies a page's theme: `usePageTheme` for a screen's binding and a door; exported for the proof gallery and the specs. */
export function applyPageTheme(theme: PageTheme): void {
  page = theme;
  paint();
  notify();
}

/** Remembers the device's choice and paints it where the page has both modes. `mode.ts` wraps it in the crossfade. */
export function chooseMode(next: ThemeMode): void {
  chosen = next;
  writeDeviceChoice(MODE_STORAGE_KEY, next === 'night' ? 'noche' : 'dia');
  paint();
  notify();
}

/** A screen's (or a door's) theme, applied at its first commit: after its chunk and stylesheets, before it paints. */
export function usePageTheme(theme: PageTheme): void {
  const { modes } = theme;
  useLayoutEffect(() => { applyPageTheme({ modes }); }, [modes]);
}

/** The mode the page paints, live. */
export function useEffectiveMode(): ThemeMode {
  return useSyncExternalStore(subscribePageTheme, () => effectiveMode(), () => 'day');
}

/** @internal For the specs: forgets the page and the choice read in this visit. */
export function resetPageThemeForTests(): void {
  page = null;
  chosen = null;
  painted = false;
  notify();
}
